
import {
  defineComponent,
  ref,
  computed,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import SelectData from "@/components/Inputs/interface/IInputSelect";
import { escolasPerfilDeDados } from "@/core/listas/escolasList";

export default defineComponent({
  name: "EscolaPerfilDeDados",
  props: {
    modelValue: {
      type: [String, Number],
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: "select",
    },
    label: {
      type: String,
      default: "Escola:",
    },
    placeholder: {
      type: String,
      default: "Selecione uma das opções",
    },
    dropdownWidth: {
      type: String,
      default: "100%",
      validator: (value: string) => {
        return /^(\d+(%|px|em|rem|vh|vw))|auto$/.test(value);
      },
    },
    // NOVA PROP: coordenadoriaId para buscar escolas automaticamente
    coordenadoriaId: {
      type: [String, Number],
      default: null,
    },
    autoFetch: {
      type: Boolean,
      default: true, // Se deve buscar automaticamente
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "change", "escolasLoaded"],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const searchQuery = ref("");
    const dropdownRef = ref<HTMLElement | null>(null);
    const searchInput = ref<HTMLInputElement | null>(null);
    const optionsContainer = ref<HTMLElement | null>(null);
    const isTouched = ref(false);
    const isLoading = ref(false);

    // Lista interna de escolas
    const escolas = ref<SelectData[]>([]);

    const visibleCount = ref(20);
    const increment = 20;

    // Função para buscar escolas
    const pegarEscolas = async () => {
      try {
        if (!props.coordenadoriaId) {
          console.warn("coordenadoriaId não fornecido para buscar escolas");
          escolas.value = [];
          return;
        }

        isLoading.value = true;
        const resultado = await escolasPerfilDeDados(props.coordenadoriaId);

        escolas.value = resultado;

        // Seleção automática se houver apenas 1 escola
        if (resultado.length === 1 && !selectedValue.value) {
          selectedValue.value = resultado[0].value;
          emit("change", { target: { value: resultado[0].value } });
        }

        // Emite evento para o pai saber que as escolas foram carregadas
        emit("escolasLoaded", resultado);
      } catch (error) {
        console.error("Erro ao carregar escolas:", error);
        escolas.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    // Watcher para reativar busca quando coordenadoriaId muda
    watch(
      () => props.coordenadoriaId,
      (newCoordenadoriaId, oldCoordenadoriaId) => {
        if (
          newCoordenadoriaId !== oldCoordenadoriaId &&
          oldCoordenadoriaId !== undefined &&
          !props.isEditMode
        ) {
          selectedValue.value = "";
          emit("change", { target: { value: "" } });
        }

        // Busca novas escolas se autoFetch está ativo
        if (props.autoFetch && newCoordenadoriaId) {
          pegarEscolas();
        } else if (!newCoordenadoriaId) {
          // Se não tem coordenadoria, limpa escolas
          escolas.value = [];
        }
      },
      { immediate: true }
    );

    watch(searchQuery, () => {
      visibleCount.value = 20;
    });

    const selectedValue = computed({
      get: () => props.modelValue,
      set: (newValue: string | number) => {
        emit("update:modelValue", newValue);
      },
    });

    // Usa escolas internas
    const options = computed(() => {
      return escolas.value;
    });

    const selectedLabel = computed(() => {
      if (!Array.isArray(options.value)) return "";
      if (selectedValue.value == undefined) return "";
      const selectedOption = options.value.find(
        (option) => option.value === props.modelValue
      );
      return selectedOption ? selectedOption.label : "";
    });

    const filteredOptions = computed(() => {
      return options.value.filter(({ label }) =>
        label?.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    const displayedOptions = computed(() => {
      let optionsList = props.required
        ? [...filteredOptions.value]
        : [{ value: "", label: " " }, ...filteredOptions.value];

      return optionsList.slice(0, visibleCount.value);
    });

    const borderStyle = computed(() => {
      if (!isTouched.value) return "";
      if (props.required && !selectedValue.value) {
        return "1px solid red";
      } else if (!props.required) {
        return "1px solid green";
      }
      return "";
    });

    const toggleDropdown = () => {
      if (isLoading.value) return; // Não abre se está carregando

      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        searchQuery.value = "";
        visibleCount.value = 20;
        nextTick(() => {
          searchInput.value?.focus();
        });
      }
    };

    const selectOption = (value: any, label: any) => {
      selectedValue.value = value;
      emit("change", { target: { value } });
      isOpen.value = false;
    };

    const handleClickOutside = (event: Event) => {
      if (
        dropdownRef.value &&
        !dropdownRef.value.contains(event.target as Node)
      ) {
        isOpen.value = false;
      }
    };

    const selectFirstOption = () => {
      if (filteredOptions.value.length > 0) {
        const { value, label } = filteredOptions.value[0];
        selectOption(value, label);
      }
    };

    const onScroll = () => {
      const container = optionsContainer.value;
      if (container) {
        const { scrollTop, clientHeight, scrollHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 20) {
          if (visibleCount.value < filteredOptions.value.length) {
            visibleCount.value = Math.min(
              visibleCount.value + increment,
              filteredOptions.value.length
            );
          }
        }
      }
    };

    document.addEventListener("click", handleClickOutside);
    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      isOpen,
      searchQuery,
      filteredOptions,
      displayedOptions,
      toggleDropdown,
      selectOption,
      selectedLabel,
      dropdownRef,
      searchInput,
      optionsContainer,
      selectFirstOption,
      borderStyle,
      onScroll,
      isLoading,
      pegarEscolas, // Expõe para uso externo se necessário
    };
  },
});
